/* Mix & Match bundle builder for the pdp-bundle template.
 *
 * Cart contract: one paid pack variant carries the bundle price. Selected
 * shapes are added as $0 component lines for fulfillment and are also recorded
 * on the parent as readable line-item properties.
 */
(function () {
  const copyElement = document.querySelector('[data-pricing-preview-copy]');
  const offerCopy = copyElement ? JSON.parse(copyElement.textContent) : {};
  const copyDefaults = {
    weeks: 'Every [count] weeks',
    summary: '[first] today. [renewal] on refills every [weeks] weeks.',
    unit: '[price]/bag',
    upsell: 'Add [count] more bags for [price]/bag'
  };
  function offerText(key, values) {
    return Object.keys(values || {}).reduce(function (text, key) {
      return text.replace('[' + key + ']', values[key]);
    }, String(offerCopy[key] || copyDefaults[key] || ''));
  }
  const RELEASE_PACKS = {
    4: { productId: 9863374078271, variantId: 50327463821631, price: 4800, plans: [10185244991, 10185277759, 10185310527, 10185343295] },
    6: { productId: 9879410278719, variantId: 50327509729599, price: 6000, plans: [10185376063, 10185408831, 10185441599, 10185474367] },
    12: { productId: 9863378305343, variantId: 50327462379839, price: 11400, plans: [10185507135, 10185539903, 10185572671, 10185605439] }
  };

  const COMPONENT_BY_SKU = {
    'spaghetti-lc-1ct-shopify': 53960650621247,
    'spaghetti-k-1ct-shopify': 53960651604287,
    'penne-lc-1ct-shopify': 53960651505983,
    'rotini-lc-1ct-shopify': 53960651538751,
    'elbows-lc-1ct-shopify': 53960651571519,
    'fettuccine-lc-1ct-shopify': 53960651637055,
    'linguine-lc-1ct-shopify': 53960651669823,
    'gnocchi-keto-1ct-shopify': 53960651702591,
    'gnocchi-pro-1ct-shopify': 53960651735359,
    'gnocchi-lite-1ct-shopify': 53960651768127,
    'lasagna-lc-1ct-shopify': 53960651800895,
  };

  // The picker uses unchanged standalone SKUs; fulfillment uses component SKUs.
  const COMPONENT_SKU_BY_VARIANT = {
    '53960650621247': 'KETO-SPA-1',
    '53960651505983': 'KETO-PEN-1',
    '53960651538751': 'KETO-ROT-1',
    '53960651571519': 'KETO-ELB-1',
    '53960651604287': 'UNO-SPA-1',
    '53960651637055': 'KETO-FET-1',
    '53960651669823': 'KETO-LIN-1',
    '53960651702591': 'KETO-GNO-1',
    '53960651735359': 'PRO-GNO-1',
    '53960651768127': 'LITE-GNO-1',
    '53960651800895': 'KETO-LAS-1',
  };

  function money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: offerCopy.currency || 'USD'
    }).format(cents / 100);
  }

  function discountPercent(comparePrice, price) {
    comparePrice = Number(comparePrice || 0);
    price = Number(price || 0);
    if (!comparePrice || price >= comparePrice) return 0;

    return Math.round(((comparePrice - price) / comparePrice) * 100);
  }

  class PdpBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.packs = JSON.parse(this.querySelector('[data-pdpb-packs]').textContent)
        .filter((pack) => RELEASE_PACKS[pack.size] && Number(pack.variantId) === RELEASE_PACKS[pack.size].variantId)
        .sort((a, b) => a.size - b.size);
      if (!this.packs.length) {
        this.querySelector('[data-pdpb-status]').textContent = 'Mix & Match is temporarily unavailable.';
        return;
      }
      this.counts = {};
      this.submitting = false;
      this.cartError = '';

      this.items = Array.from(this.querySelectorAll('[data-pdpb-item]'));
      this.items.forEach((item) => {
        const id = item.dataset.variantId;
        item.dataset.componentVariantId = COMPONENT_BY_SKU[(item.dataset.sku || '').trim()] || '';
        item.querySelector('[data-pdpb-add]').addEventListener('click', () => this.change(id, 1));
        item.querySelector('[data-pdpb-plus]').addEventListener('click', () => this.change(id, 1));
        item.querySelector('[data-pdpb-minus]').addEventListener('click', () => this.change(id, -1));
      });

      this.checkoutButton = this.querySelector('[data-pdpb-checkout]');
      this.checkoutButton.addEventListener('click', () => this.addToCart());

      // Keep delivery interval when the selected pack changes.
      const initialPurchase = this.querySelector('[data-pdpb-purchase]:checked');
      this.isSubscription = Boolean(initialPurchase && initialPurchase.value === 'subscribe');
      this.planInterval = 'week:4';
      this.renderedPlansFor = null;
      this.planSelect = this.querySelector('[data-pdpb-plan-select]');
      this.querySelectorAll('[data-pdpb-purchase]').forEach((input) => {
        input.addEventListener('change', () => {
          this.isSubscription = input.value === 'subscribe' && input.checked;
          this.render();
        });
      });
      if (this.planSelect) {
        this.planSelect.addEventListener('change', () => {
          const plan = (this.targetPack.plans || []).find((candidate) => String(candidate.id) === this.planSelect.value);
          if (plan) this.planInterval = this.intervalKey(plan);
          this.render();
        });
      }

      this.render();
      this.refreshAvailability();
    }

    intervalKey(plan) {
      if (Number(plan.weeks) > 0) return 'week:' + Number(plan.weeks);
      const label = String(plan.label || '').trim().toLowerCase();
      const weeks = label.match(/\b(\d+)\s*weeks?\b/);
      return weeks ? 'week:' + Number(weeks[1]) : 'label:' + label;
    }

    validPlans(pack) {
      const release = RELEASE_PACKS[pack.size];
      return (pack.plans || []).filter((plan) => release.plans.includes(Number(plan.id))
        && Number(plan.price) === Math.round(Number(pack.oneTimePrice) * 0.8)
        && Number(plan.renewalPrice) === Math.round(Number(pack.oneTimePrice) * 0.85));
    }

    planFor(pack) {
      const plans = this.validPlans(pack);
      return plans.find((plan) => this.intervalKey(plan) === this.planInterval)
        || plans.find((plan) => this.intervalKey(plan) === 'week:4')
        || plans[0] || null;
    }

    get selectedPlan() {
      return this.planFor(this.targetPack);
    }

    get oneTimeTotal() {
      const pack = Number(this.targetPack.oneTimePrice) || 0;
      return pack || this.selectionTotal();
    }

    subscriptionTotal(percent) {
      return Math.round(this.oneTimeTotal * (1 - (percent || 0) / 100));
    }

    displayTotal() {
      const plan = this.isSubscription ? this.selectedPlan : null;
      if (!plan) return this.oneTimeTotal;
      return Number(plan.price) || this.subscriptionTotal(plan.percent);
    }

    syncPlans() {
      if (!this.planSelect) return;
      const target = this.targetPack;
      if (this.renderedPlansFor === target.size) return;
      this.renderedPlansFor = target.size;
      const selected = this.planFor(target);
      this.planSelect.innerHTML = '';
      this.validPlans(target).forEach((plan) => {
        const option = document.createElement('option');
        option.value = plan.id;
        option.textContent = plan.label;
        this.planSelect.appendChild(option);
      });
      this.planSelect.value = selected ? String(selected.id) : '';
      this.planSelect.disabled = !selected || this.submitting;
      if (selected) this.planInterval = this.intervalKey(selected);
      const badge = this.querySelector('[data-pdpb-sub-badge]');
      if (badge && this.selectedPlan && this.selectedPlan.percent) {
        badge.textContent = 'Save ' + this.selectedPlan.percent + '%';
      }
    }

    get count() {
      return Object.values(this.counts).reduce((sum, n) => sum + n, 0);
    }

    get maxSize() {
      return this.packs[this.packs.length - 1].size;
    }

    /* smallest pack that fits the current count — the "jump to the next pack" rule */
    get targetPack() {
      return this.packs.find((pack) => this.count <= pack.size) || this.packs[this.packs.length - 1];
    }

    get isExactPack() {
      return this.packs.some((pack) => pack.size === this.count);
    }

    change(variantId, delta) {
      const item = this.items.find((candidate) => candidate.dataset.variantId === String(variantId));
      if (delta > 0 && item && item.dataset.available === 'false') return;
      if (this.submitting) return;
      const next = (this.counts[variantId] || 0) + delta;
      if (next < 0) return;
      if (delta > 0 && this.count >= this.maxSize) return;
      this.cartError = '';
      this.counts[variantId] = next;
      this.render();
    }

    async readItemAvailability(item) {
      const componentVariantId = item.dataset.componentVariantId;
      if (!componentVariantId) return false;

      try {
        const response = await window.fetch('/variants/' + componentVariantId + '.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) return false;
        const variant = await response.json();
        return Number(variant.id) === Number(componentVariantId) && variant.available === true
          && Number(variant.price) === 0 && variant.requires_shipping === true;
      } catch (error) {
        console.warn('[Pete\'s Mix & Match] Availability check failed', error);
        return false;
      }
    }

    setItemAvailability(item, available) {
      const variantId = item.dataset.variantId;
      item.dataset.available = String(available);
      item.classList.toggle('is-unavailable', !available);
      item.setAttribute('aria-disabled', String(!available));

      const add = item.querySelector('[data-pdpb-add]');
      add.disabled = !available;
      add.textContent = available ? 'Add +' : 'Sold out';

      if (!available && this.counts[variantId]) {
        delete this.counts[variantId];
      }
    }

    async refreshAvailability(items = this.items) {
      const results = await Promise.all(
        items.map(async (item) => ({ item, available: await this.readItemAvailability(item) }))
      );
      results.forEach(({ item, available }) => this.setItemAvailability(item, available));
      this.render();
      return results.filter(({ available }) => available === false).map(({ item }) => item);
    }

    friendlyCartError(message) {
      const soldOut = String(message || '').match(/The product '(.+?)(?: \(Bundle Component\))?' is already sold out\.?/i);
      if (soldOut) {
        return soldOut[1] + ' is temporarily unavailable for Mix & Match. Please remove it and try again.';
      }

      return message || 'We could not add this bundle. Please try again or contact us for help.';
    }

    selectionTotal() {
      return this.items.reduce((sum, item) => {
        return sum + (this.counts[item.dataset.variantId] || 0) * Number(item.dataset.price);
      }, 0);
    }

    updatePurchaseDisplay() {
      const target = this.targetPack;
      const compareAt = Number(target.compareAt) || 0;
      const oneTime = this.oneTimeTotal;
      const plan = this.selectedPlan;
      const subPrice = Number(plan && plan.price) || this.subscriptionTotal(plan && plan.percent);
      const renewalPrice = Number(plan && plan.renewalPrice) || subPrice;
      const subscriptionDiscount = discountPercent(oneTime, subPrice);
      const renewalDiscount = discountPercent(oneTime, renewalPrice);

      this.classList.toggle('is-subscription', this.isSubscription);
      this.querySelectorAll('[data-simple-cadence-copy]').forEach((el) => {
        el.textContent = offerText('weeks', {count: plan && plan.weeks || (plan && this.intervalKey(plan).replace('week:', '')) || 4});
      });

      const setRow = (priceSel, compareSel, price, reference) => {
        const priceEl = this.querySelector(priceSel);
        const compareEl = this.querySelector(compareSel);
        if (priceEl) priceEl.textContent = money(price);
        if (compareEl) {
          if (reference > price) {
            compareEl.textContent = money(reference);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      };
      setRow('[data-pdpb-onetime-price]', '[data-pdpb-onetime-compare]', oneTime, compareAt);
      setRow('[data-pdpb-sub-price]', '[data-pdpb-sub-compare]', subPrice, oneTime);

      const subBadge = this.querySelector('[data-pdpb-sub-badge]');
      const badgeDiscount = subscriptionDiscount;
      if (subBadge) {
        subBadge.hidden = !badgeDiscount;
        if (badgeDiscount) subBadge.textContent = 'Save ' + badgeDiscount + '%';
      }
      this.querySelectorAll('[data-pdpb-subscription-today-discount]').forEach((el) => {
        el.textContent = subscriptionDiscount + '%';
      });
      this.querySelectorAll('[data-pdpb-subscription-renewal-discount]').forEach((el) => {
        el.textContent = renewalDiscount + '%';
      });

      this.querySelectorAll('[data-pdpb-subscription-heading]').forEach((el) => {
        el.textContent = 'Extra ' + subscriptionDiscount + '% off your first order';
      });
      this.querySelectorAll('[data-pdpb-subscription-summary]').forEach((el) => {
        el.textContent = offerText('summary', {first: money(subPrice), renewal: money(renewalPrice), weeks: plan && plan.weeks || (plan && this.intervalKey(plan).replace('week:', '')) || 4});
      });
      this.querySelectorAll('[data-pdpb-one-time-unit]').forEach((el) => {
        el.textContent = money(Math.round(oneTime / target.size)) + '/bag';
      });
      this.querySelectorAll('[data-pdpb-tier]').forEach((tier) => {
        const size = Number(tier.dataset.size);
        const pack = this.packs.find((candidate) => candidate.size === size);
        const oneTimeEl = tier.querySelector('[data-pdpb-tier-off="onetime"]');
        const subEl = tier.querySelector('[data-pdpb-tier-off="subscribe"]');
        if (oneTimeEl) {
          const tierPlan = pack && this.planFor(pack);
          const tierPrice = pack && (this.isSubscription && tierPlan ? tierPlan.price : pack.oneTimePrice);
          oneTimeEl.textContent = pack ? offerText('unit', {price: money(Math.round(tierPrice / size))}) : '';
          oneTimeEl.hidden = !pack;
        }
        if (subEl) subEl.hidden = true;
      });
    }

    render() {
      const count = this.count;
      const target = this.targetPack;
      this.syncPlans();
      this.updatePurchaseDisplay();

      this.items.forEach((item) => {
        const n = this.counts[item.dataset.variantId] || 0;
        const available = item.dataset.available !== 'false';
        item.classList.toggle('has-qty', n > 0);
        item.querySelector('[data-pdpb-count]').textContent = n;
        item.querySelector('[data-pdpb-add]').hidden = n > 0;
        item.querySelector('[data-pdpb-add]').disabled = !available || this.submitting;
        item.querySelector('.pdp-bundle__builder-stepper').hidden = n === 0;
        item.querySelector('[data-pdpb-plus]').disabled = !available || this.submitting || count >= this.maxSize;
        item.querySelector('[data-pdpb-minus]').disabled = this.submitting;
      });

      this.querySelectorAll('[data-pdpb-tier]').forEach((tier) => {
        const size = Number(tier.dataset.size);
        tier.classList.toggle('is-reached', count >= size);
        tier.classList.toggle('is-target', size === target.size && count > 0);
      });

      const status = this.querySelector('[data-pdpb-status]');
      const upsell = this.querySelector('[data-pdpb-upsell]');
      const text = this.querySelector('[data-pdpb-checkout-text]');
      const remaining = target.size - count;

      upsell.hidden = true;
      status.removeAttribute('role');
      if (this.cartError) {
        status.textContent = this.cartError;
        status.setAttribute('role', 'alert');
      } else if (count === 0) {
        status.textContent = 'Add ' + target.size + ' bags to start your bundle';
        text.textContent = 'Add ' + target.size + ' bags';
      } else if (this.isExactPack) {
        status.textContent = count + ' bag ' + target.size + '-pack bundle complete!';
        text.textContent = 'Add to Cart - ' + money(this.displayTotal());
        const next = this.packs[this.packs.indexOf(target) + 1];
        if (next) {
          const extra = next.size - count;
          upsell.textContent = offerText('upsell', {count: extra, price: money(Math.round((this.isSubscription && this.planFor(next) ? this.planFor(next).price : next.oneTimePrice) / next.size))});
          upsell.hidden = false;
        }
      } else {
        status.textContent = count + '/' + target.size + ' bags - add ' + remaining + ' more for the ' + target.size + '-pack';
        text.textContent = 'Add ' + remaining + ' more bag' + (remaining === 1 ? '' : 's');
      }
      const readinessError = this.readinessError();
      this.checkoutButton.disabled = !this.isExactPack || this.submitting || Boolean(readinessError);
      this.querySelectorAll('[data-pdpb-purchase]').forEach((input) => { input.disabled = this.submitting; });
      if (this.planSelect) this.planSelect.disabled = this.submitting || !this.selectedPlan;
      if (!this.cartError && this.isExactPack && readinessError) {
        status.textContent = readinessError;
        status.setAttribute('role', 'status');
      }
      if (this.submitting) text.textContent = 'Adding...';
    }

    readinessError() {
      const pack = this.targetPack;
      const release = RELEASE_PACKS[pack.size];
      if (!pack.available || Number(pack.productId) !== release.productId
        || Number(pack.variantId) !== release.variantId || !(Number(pack.oneTimePrice) > 0)
        || (pack.currency === 'USD' && Number(pack.oneTimePrice) !== release.price)) {
        return 'This pack is temporarily unavailable. Please try again later.';
      }
      const plan = this.isSubscription ? this.selectedPlan : null;
      if (this.isSubscription && !plan) return 'Subscriptions are temporarily unavailable for this pack.';
      const selected = this.items.filter((item) => (this.counts[item.dataset.variantId] || 0) > 0);
      for (const item of selected) {
        const componentId = Number(item.dataset.componentVariantId);
        const component = (pack.components || []).find((candidate) => Number(candidate.id) === componentId);
        if (!COMPONENT_SKU_BY_VARIANT[componentId] || !component || !component.available || item.dataset.available === 'false') {
          return 'A selected shape is temporarily unavailable. Please choose another shape.';
        }
        if (plan && !(component.plans || []).some((id) => Number(id) === Number(plan.id))) {
          return 'A selected shape is unavailable for this delivery schedule. Please choose another schedule or shape.';
        }
      }
      return '';
    }

    async verifyParent() {
      const pack = this.targetPack;
      const response = await fetch('/products/' + encodeURIComponent(pack.handle) + '.js', {
        headers: { Accept: 'application/json' }, cache: 'no-store'
      });
      if (!response.ok) throw new Error('This pack is temporarily unavailable. Please refresh and try again.');
      const product = await response.json();
      const variant = (product.variants || []).find((candidate) => Number(candidate.id) === Number(pack.variantId));
      if (Number(product.id) !== Number(pack.productId) || !variant || !variant.available
        || variant.requires_shipping !== false || Number(variant.price) !== Number(pack.oneTimePrice)) {
        throw new Error('This pack has changed or is temporarily unavailable. Please refresh and try again.');
      }
      const plan = this.isSubscription ? this.selectedPlan : null;
      if (plan) {
        const allocation = (variant.selling_plan_allocations || []).find((candidate) => Number(candidate.selling_plan_id) === Number(plan.id));
        const adjustments = allocation && allocation.price_adjustments || [];
        const renewal = adjustments[adjustments.length - 1];
        if (!allocation || Number(allocation.price) !== Number(plan.price)
          || !renewal || Number(renewal.price) !== Number(plan.renewalPrice)) {
          throw new Error('This delivery schedule has changed. Please refresh and try again.');
        }
      }
    }

    async addToCart() {
      if (!this.isExactPack || this.submitting || this.readinessError()) return;
      this.submitting = true;
      this.cartError = '';
      this.render();

      const selectedItems = this.items.filter((item) => (this.counts[item.dataset.variantId] || 0) > 0);
      let unavailableItems;
      try {
        await this.verifyParent();
        unavailableItems = await this.refreshAvailability(selectedItems);
        if (this.readinessError()) throw new Error(this.readinessError());
      } catch (error) {
        this.cartError = this.friendlyCartError(error && error.message);
        this.submitting = false;
        this.render();
        return;
      }
      if (unavailableItems.length) {
        const names = unavailableItems.map((item) => item.dataset.flavor || 'A selected item');
        this.cartError = names.join(', ') +
          (names.length === 1 ? ' is' : ' are') +
          ' temporarily unavailable for Mix & Match. Please choose another shape.';
        this.submitting = false;
        this.render();
        return;
      }

      const text = this.querySelector('[data-pdpb-checkout-text]');
      const original = text.textContent;
      text.textContent = 'Adding...';

      const bundleId = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 11);
      const plan = this.isSubscription ? this.selectedPlan : null;
      const properties = {
        _bundle: bundleId,
        _bundle_main: 'true',
        _bundle_id: bundleId,
        _bundle_parent: true,
        _bundle_min: this.targetPack.size,
        _bundle_max: this.targetPack.size,
        _bundle_size: this.targetPack.size,
      };
      let propertyIndex = 1;

      this.items.forEach((item) => {
        const n = this.counts[item.dataset.variantId] || 0;
        if (n === 0) return;
        const flavor = item.dataset.flavor || 'Pasta';
        properties['Flavor ' + propertyIndex] = n + ' x ' + flavor + ' 1 Pack';
        propertyIndex += 1;
      });

      const items = [
        {
          id: this.targetPack.variantId,
          quantity: 1,
          properties: properties,
        },
      ];
      if (plan) {
        items[0].selling_plan = Number(plan.id);
        items[0].properties._subscription = 'true';
      }

      /* OMG fix: also add the selected shapes as real $0 component SKU lines
         (fulfillment reads SKUs on line items; Flavor props are display-only). */
      this.items.forEach((item) => {
        const n = this.counts[item.dataset.variantId] || 0;
        if (n === 0) return;
        const comp = COMPONENT_BY_SKU[(item.dataset.sku || '').trim()];
        if (!comp) return;
        const child = {
          id: comp,
          quantity: n,
          properties: { _bundle: bundleId, _bundle_id: bundleId, _bundle_size: this.targetPack.size, flavor: item.dataset.flavor || '', sku: COMPONENT_SKU_BY_VARIANT[comp] },
        };
        if (plan) {
          child.selling_plan = Number(plan.id);
          child.properties._subscription = 'true';
        }
        items.push(child);
      });

      try {
        const componentQuantity = items.slice(1).reduce((sum, item) => sum + item.quantity, 0);
        if (items.length < 2 || componentQuantity !== this.targetPack.size) {
          throw new Error('Please select exactly ' + this.targetPack.size + ' bags for this bundle.');
        }
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(function () { return null; });
          throw new Error((payload && payload.description) || 'Unable to add bundle to cart');
        }
        document.dispatchEvent(new CustomEvent('toggle-cart'));
        window.dispatchEvent(
          new CustomEvent('petes:cart-mutated', {
            detail: { source: 'pdp-bundle', itemCount: items.length },
          })
        );
        if (typeof window.updateCart === 'function') {
          await Promise.resolve(window.updateCart());
        } else {
          window.location.href = '/cart';
        }
      } catch (error) {
        console.error('[Pete\'s Mix & Match]', error);
        this.cartError = this.friendlyCartError(error && error.message);
      } finally {
        this.submitting = false;
        text.textContent = original;
        this.render();
      }
    }
  }

  if (!customElements.get('pdp-bundle-builder')) {
    customElements.define('pdp-bundle-builder', PdpBundleBuilder);
  }
})();
